import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, KpiCard, StatusPill, Button, Logo, OBLIGATION_STATUS } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { signOut } from '../lib/auth';
import { fmtDate } from '../lib/fmt';
import { useOrg } from '../lib/org';

type Prop = { id: string; name: string; property_type: string };
type Obl = { id: string; status: string; due_date: string | null; property_id: string;
  assets: { name: string } | null;
  asset_obligation_rules: { name_i18n: Record<string, string>; rule_code: string } | null };
type Req = { id: string; title: string; status: string; property_id: string; created_at: string; work_order_id: string | null };
const WO_TYPES = ['corrective', 'preventive', 'inspection', 'emergency'] as const;

export function OwnerPortal() {
  const { t, i18n } = useTranslation();
  const { timezone } = useOrg();
  const navigate = useNavigate();
  const [props, setProps] = useState<Prop[]>([]);
  const [obl, setObl] = useState<Obl[]>([]);
  const [reqs, setReqs] = useState<Req[]>([]);
  const [title, setTitle] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [busy, setBusy] = useState(false);
  // convert-to-WO modal
  const [cvReq, setCvReq] = useState<Req | null>(null);
  const [cvAssets, setCvAssets] = useState<{ id: string; name: string }[]>([]);
  const [cvTechs, setCvTechs] = useState<{ id: string; name: string }[]>([]);
  const [cv, setCv] = useState({ wo_type: 'corrective', asset_id: '', assigned_to: '' });
  const [cvBusy, setCvBusy] = useState(false);
  const [cvErr, setCvErr] = useState('');

  async function openConvert(r: Req) {
    setCvReq(r); setCv({ wo_type: 'corrective', asset_id: '', assigned_to: '' }); setCvErr('');
    const [a, m, pr] = await Promise.all([
      supabase.from('assets').select('id,name').eq('property_id', r.property_id).is('deleted_at', null).order('name'),
      supabase.from('memberships').select('user_id,role').eq('role', 'technician').is('deleted_at', null),
      supabase.from('user_profiles').select('user_id,display_name').is('deleted_at', null),
    ]);
    const names = Object.fromEntries(((pr.data as any[]) ?? []).map((p) => [p.user_id, p.display_name]));
    setCvAssets((a.data as any[]) ?? []);
    setCvTechs(((m.data as any[]) ?? []).map((x) => ({ id: x.user_id, name: names[x.user_id] ?? '—' })));
  }
  async function submitConvert() {
    if (!cvReq) return;
    setCvBusy(true); setCvErr('');
    const { data, error } = await supabase.rpc('convert_request_to_wo', {
      p_request: cvReq.id, p_wo_type: cv.wo_type, p_asset_id: cv.asset_id || null, p_assigned_to: cv.assigned_to || null,
    });
    setCvBusy(false);
    if (error) { setCvErr(error.message); return; }
    setCvReq(null);
    navigate(`/work-orders?wo=${data as string}`);
  }

  async function load() {
    const [p, o, r] = await Promise.all([
      supabase.from('properties').select('id,name,property_type').is('deleted_at', null).order('name'),
      supabase.from('asset_obligations').select('id,status,due_date,property_id,assets(name),asset_obligation_rules(name_i18n,rule_code)').is('deleted_at', null).order('due_date'),
      supabase.from('service_requests').select('id,title,status,property_id,created_at,work_order_id').is('deleted_at', null).order('created_at', { ascending: false }),
    ]);
    setProps((p.data as Prop[]) ?? []);
    setObl((o.data as unknown as Obl[]) ?? []);
    setReqs((r.data as Req[]) ?? []);
    if (!propertyId && p.data?.[0]) setPropertyId((p.data[0] as Prop).id);
  }
  useEffect(() => { load(); }, []);

  const kpis = useMemo(() => ({
    properties: props.length,
    overdue: obl.filter((o) => o.status === 'overdue').length,
    dueSoon: obl.filter((o) => o.status === 'due_soon').length,
  }), [props, obl]);

  async function submitRequest(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !propertyId) return;
    setBusy(true);
    await supabase.from('service_requests').insert({ property_id: propertyId, title: title.trim() } as any);
    setTitle('');
    setBusy(false);
    load();
  }

  return (
    <div className="min-h-screen bg-bg text-fg">
      <header className="flex items-center justify-between border-b border-border bg-card px-7 py-4">
        <Logo variant="light" size={28} />
        <button onClick={() => signOut()} className="text-sm text-muted hover:text-fg">{t('auth.signOut')}</button>
      </header>

      <main className="mx-auto flex w-full max-w-[1000px] flex-col gap-5 px-7 py-6">
        <h1 className="text-[28px] font-semibold tracking-[-0.02em]">{t('portal.title')}</h1>
        <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          <KpiCard label={t('nav.properties')} value={kpis.properties} dark />
          <KpiCard label={t('kpi.overdue')} value={<span className="text-st-overdue-fg">{kpis.overdue}</span>} />
          <KpiCard label={t('status.dueSoon')} value={<span className="text-st-onhold-fg">{kpis.dueSoon}</span>} />
        </section>

        <h2 className="eyebrow">{t('portal.myProperties')}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {props.map((p) => (
            <Card key={p.id} className="p-4">
              <div className="font-semibold">{p.name}</div>
              <div className="text-sm text-muted">{t('propType.' + p.property_type)}</div>
            </Card>
          ))}
          {props.length === 0 && <div className="text-sm text-muted">{t('common.noData')}</div>}
        </div>

        <h2 className="eyebrow">{t('nav.dueDates')}</h2>
        <Card className="overflow-hidden p-0">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {obl.slice(0, 20).map((o) => {
                const sd = OBLIGATION_STATUS[o.status] ?? OBLIGATION_STATUS.scheduled!;
                return (
                  <tr key={o.id} className="border-t border-divider first:border-0">
                    <td className="px-5 py-3">
                      <div className="font-medium">{o.asset_obligation_rules?.name_i18n?.[i18n.language] ?? o.asset_obligation_rules?.name_i18n?.en ?? o.asset_obligation_rules?.rule_code ?? '—'}</div>
                      {o.assets?.name && <div className="text-xs text-muted">{o.assets.name}</div>}
                    </td>
                    <td className="px-2 py-3 font-mono text-muted">{o.due_date ? fmtDate(o.due_date, i18n.language, timezone) : '—'}</td>
                    <td className="px-5 py-3 text-right"><StatusPill tone={sd.tone}>{t(sd.labelKey)}</StatusPill></td>
                  </tr>
                );
              })}
              {obl.length === 0 && <tr><td className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
            </tbody>
          </table>
        </Card>

        <h2 className="eyebrow">{t('portal.requests')}</h2>
        <Card className="flex flex-col gap-3 p-5">
          <form onSubmit={submitRequest} className="flex flex-wrap items-center gap-2">
            <select className="h-9 rounded-md border border-border bg-card px-2.5 text-sm" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
              {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input className="h-9 min-w-0 flex-1 rounded-md border border-border bg-card px-3 text-sm outline-none" placeholder={t('portal.newRequest')} value={title} onChange={(e) => setTitle(e.target.value)} />
            <Button variant="accent" type="submit" disabled={busy || !title.trim()}>{t('portal.send')}</Button>
          </form>
          <div className="flex flex-col divide-y divide-divider">
            {reqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate">{r.title}</span>
                {r.work_order_id ? (
                  <button onClick={() => navigate(`/work-orders?wo=${r.work_order_id}`)} className="shrink-0 rounded-pill bg-st-done-bg px-2.5 py-1 text-xs font-medium text-st-done-fg">{t('req.viewWo')} →</button>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => openConvert(r)}>{t('req.convert')}</Button>
                )}
                <span className="shrink-0 font-mono text-xs text-muted">{fmtDate(r.created_at, i18n.language, timezone)}</span>
              </div>
            ))}
            {reqs.length === 0 && <div className="py-2 text-sm text-muted">{t('common.noData')}</div>}
          </div>
        </Card>
      </main>

      {cvReq && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setCvReq(null)}>
          <Card className="w-full max-w-[420px] p-5" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <h3 className="mb-1 text-base font-semibold">{t('req.convertTitle')}</h3>
            <p className="mb-4 text-sm text-muted">{cvReq.title}</p>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm"><span className="text-muted">{t('wo.type')}</span>
                <select className="h-9 rounded-md border border-border bg-card px-2.5 text-sm" value={cv.wo_type} onChange={(e) => setCv({ ...cv, wo_type: e.target.value })}>
                  {WO_TYPES.map((w) => <option key={w} value={w}>{t('woType.' + w)}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-muted">{t('nav.assets')}</span>
                <select className="h-9 rounded-md border border-border bg-card px-2.5 text-sm" value={cv.asset_id} onChange={(e) => setCv({ ...cv, asset_id: e.target.value })}>
                  <option value="">{t('common.none')}</option>
                  {cvAssets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-muted">{t('wo.assignedTo')}</span>
                <select className="h-9 rounded-md border border-border bg-card px-2.5 text-sm" value={cv.assigned_to} onChange={(e) => setCv({ ...cv, assigned_to: e.target.value })}>
                  <option value="">{t('common.none')}</option>
                  {cvTechs.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </label>
              {cvErr && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{cvErr}</div>}
              <div className="mt-1 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setCvReq(null)}>{t('common.cancel')}</Button>
                <Button variant="accent" disabled={cvBusy} onClick={submitConvert}>{t('req.convert')}</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
